import React, { ReactNode } from 'react';

// Repurposed from https://github.com/pablosichert/react-truncate/
export const Truncate = ({
  children,
  ellipsis,
  lines = 1,
  width,
  onTruncate,
}: {
  children: React.ReactNode;
  ellipsis: React.ReactNode;
  lines?: number;
  width?: number;
  onTruncate?: (truncated: boolean) => void;
}) => {
  const targetRef = React.useRef<HTMLSpanElement>(null);
  const textRef = React.useRef<HTMLSpanElement>(null);
  const ellipsisRef = React.useRef<HTMLSpanElement>(null);
  const [targetWidth, setTargetWidth] = React.useState<number>(0);
  const [canvasContext, setCanvasContext] =
    React.useState<CanvasRenderingContext2D | null>(null);
  const [text, setText] = React.useState<React.ReactNode>('');

  let timeout: number;

  const calcTargetWidth = (callback?: Function) => {
    // Calculation is no longer relevant, since node has been removed
    if (!targetRef.current) {
      return;
    }

    const targetWidth =
      width ||
      // Floor the result to deal with browser subpixel precision
      Math.floor(
        targetRef.current.parentElement?.getBoundingClientRect().width || 0,
      );

    // Delay calculation until parent node is inserted to the document
    // Mounting order in React is ChildComponent, ParentComponent
    if (!targetWidth) {
      return window.requestAnimationFrame(() => calcTargetWidth(callback));
    }

    if (canvasContext) {
      const style = window.getComputedStyle(
        targetRef.current,
      ) as CSSStyleDeclaration;

      const font = [
        style.fontWeight,
        style.fontStyle,
        style.fontSize,
        style.fontFamily,
      ].join(' ');

      canvasContext.font = font;

      setCanvasContext(canvasContext);
    }

    setTargetWidth(targetWidth);
    callback?.();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: onMount
  React.useEffect(() => {
    const canvas = document.createElement('canvas');
    setCanvasContext(canvas.getContext('2d'));

    calcTargetWidth(() => {
      // Node not needed in document tree to read its content
      textRef?.current?.parentNode?.removeChild(textRef.current);
    });

    window.addEventListener('resize', () => calcTargetWidth());

    return () => {
      ellipsisRef.current?.parentNode?.removeChild(ellipsisRef.current);

      window.removeEventListener('resize', () => calcTargetWidth());

      window.cancelAnimationFrame(timeout);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: function isn't a callback and would cause re-renders every time
  React.useEffect(() => {
    calcTargetWidth();
  }, [width, canvasContext]);

  const handleTruncate = (didTruncate: boolean) => {
    timeout = window.requestAnimationFrame(() => {
      onTruncate?.(didTruncate);
    });
  };

  const renderLine = (line: ReactNode, i: number, arr: Array<any>) => {
    if (i === arr.length - 1) {
      return <span key={i}>{line}</span>;
    } else {
      const br = <br key={`${i}br`} />;

      if (line) {
        return [<span key={i}>{line}</span>, br];
      } else {
        return br;
      }
    }
  };

  // Shim innerText to consistently break lines at <br/> but not at \n
  const getInnerText = (node: HTMLSpanElement | null) => {
    const div = document.createElement('div');
    const contentKey =
      'innerText' in window.HTMLElement.prototype ? 'innerText' : 'textContent';

    div.innerHTML = node?.innerHTML.replace(/\r\n|\r|\n/g, ' ') || '';

    let text = div[contentKey];

    const test = document.createElement('div');
    test.innerHTML = 'foo<br/>bar';

    if (test[contentKey]?.replace(/\r\n|\r/g, '\n') !== 'foo\nbar') {
      div.innerHTML = div.innerHTML.replace(/<br.*?[\/]?>/gi, '\n');
      text = div[contentKey];
    }

    return text;
  };

  const measureWidth = (text: string) => {
    return canvasContext?.measureText(text).width || 0;
  };

  const getEllipsisWidth = (node: HTMLSpanElement | null) =>
    node?.offsetWidth || 0;

  const getLines = () => {
    const linesArray = [];
    const text = getInnerText(textRef.current);
    const textLines = text?.split('\n').map((line: string) => line.split(' '));
    let didTruncate = true;
    const ellipsisWidth = getEllipsisWidth(ellipsisRef.current);

    for (let line = 1; line <= lines; line++) {
      const textWords = textLines?.[0];

      // Handle newline
      if (textWords?.length === 0) {
        linesArray.push();
        textLines?.shift();
        line--;
        continue;
      }

      const textWordLine: string = textWords?.join(' ') || '';

      if (measureWidth(textWordLine) <= targetWidth) {
        if (textLines?.length === 1) {
          // Line is end of text and fits without truncating
          didTruncate = false;

          linesArray.push(textWordLine);
          break;
        }
      }

      let resultLine: ReactNode;

      if (textWords) {
        if (line === lines) {
          // Binary search determining the longest possible line inluding truncate string
          const textRest = textWords.join(' ');

          let lower = 0;
          let upper = textRest.length - 1;

          while (lower <= upper) {
            const middle = Math.floor((lower + upper) / 2);

            const testLine = textRest.slice(0, middle + 1);

            if (measureWidth(testLine) + ellipsisWidth <= targetWidth) {
              lower = middle + 1;
            } else {
              upper = middle - 1;
            }
          }

          const lastLineText = textRest.slice(0, lower);

          resultLine = (
            <span>
              {lastLineText}
              {ellipsis}
            </span>
          );
        } else {
          // Binary search determining when the line breaks
          let lower = 0;
          let upper = textWords.length - 1;

          while (lower <= upper) {
            const middle = Math.floor((lower + upper) / 2);

            const testLine = textWords.slice(0, middle + 1).join(' ');

            if (measureWidth(testLine) <= targetWidth) {
              lower = middle + 1;
            } else {
              upper = middle - 1;
            }
          }

          // The first word of this line is too long to fit it
          if (lower === 0) {
            // Jump to processing of last line
            line = lines - 1;
            continue;
          }

          resultLine = textWords.slice(0, lower).join(' ');
          textLines[0].splice(0, lower);
        }
      }

      linesArray.push(resultLine);
    }

    handleTruncate(didTruncate);

    return linesArray;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: functions aren't callbacks and would cause constant re-rendering
  React.useEffect(() => {
    const mounted = !!(targetRef && targetWidth);

    if (typeof window !== 'undefined' && mounted) {
      if (lines > 0) {
        setText(getLines().map(renderLine));
      } else {
        setText(children);

        handleTruncate(false);
      }
    }
  }, [children, targetWidth, canvasContext, lines]);

  return (
    <span ref={targetRef}>
      <span data-testid="Truncate-text">{text}</span>
      <span ref={textRef}>{children}</span>
      <span
        ref={ellipsisRef}
        style={{
          position: 'fixed',
          visibility: 'hidden',
          top: 0,
          left: 0,
        }}
      >
        {ellipsis}
      </span>
    </span>
  );
};
